/* eslint-disable no-extra-boolean-cast */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import {
  Search as SearchIcon,
  Settings as SettingsIcon,
  XCircle as XCircleIcon,
  ArrowRightCircle as ArrowIcon,
} from 'react-feather';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';

import {
  setSubcategoryQuery,
  setSubcategoryCategory,
  setSubcategoryPage,
  setSubcategoryLimit,
  setSubcategorySupermarket,
} from '../../../actions/subcategoriesList';

import {
  setProductQuery,
  setProductSupermarket,
  setProductCategory,
  setProductSubcategory,
  setProductPage,
  setProductLimit,
  setProductBrand,
} from '../../../actions/productsList';

import useStyles from './styles';
import alphabeticSort from '../../../utils/alphabeticSort';

function Results({
  className,
  subcategories,
  supermarkets,
  categories,
  setCategories,
  originalCategories,
  count,
  allSearchResults,
  ...rest
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentCategories, setCurrentCategories] = useState();
  const [currentSubcategories, setCurrentSubcategories] = useState(subcategories || []);

  const {
    subcategoryCategory,
    subcategorySupermarket,
    subcategoryPage,
    subcategoryLimit,
    subcategoryQuery,
  } = useSelector((state) => state.subcategoriesList);

  const [filteredSupermarkets, setFilteredSupermarkets] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);

  useEffect(() => { setCurrentSubcategories(subcategories || []); }, [subcategories]);

  useEffect(() => {
    if (subcategoryQuery && subcategoryQuery.trim() !== '' && allSearchResults.length > 0) {
      const uniqueSupermarketIds = [...new Set(allSearchResults.map((sub) => sub.Supermarket?.id).filter(Boolean))];
      const uniqueCategoryIds = [...new Set(allSearchResults.map((sub) => sub.Category?.id).filter(Boolean))];

      const filteredSup = supermarkets.filter((sup) => uniqueSupermarketIds.includes(sup.id));
      const filteredCat = originalCategories.filter((cat) => uniqueCategoryIds.includes(cat.id));

      setFilteredSupermarkets(filteredSup.sort((a, b) => alphabeticSort(a, b)));
      setFilteredCategories(filteredCat.sort((a, b) => alphabeticSort(a, b)));
    } else {
      setFilteredSupermarkets([]);
      setFilteredCategories([]);
    }
  }, [subcategoryQuery, allSearchResults, supermarkets, originalCategories]);

  const handleQueryChange = (event) => {
    event.persist();
    setCurrentQuery(event.target.value);
  };

  useEffect(() => {
    setCurrentCategories(categories);
  }, [categories]);

  const getAvailableSupermarkets = () => {
    if (subcategoryQuery && subcategoryQuery.trim() !== '' && filteredSupermarkets.length > 0) {
      return filteredSupermarkets;
    }
    return supermarkets;
  };

  const getAvailableCategories = () => {
    if (!filteredCategories || filteredCategories.length === 0) {
      return [];
    }
    if (subcategoryQuery && subcategoryQuery.trim() !== '' && filteredCategories.length > 0) {
      return filteredCategories;
    }
    return currentCategories;
  };

  const getValidatedSupermarketValue = () => {
    if (!subcategorySupermarket || subcategorySupermarket.length === 0) {
      return [];
    }
    const availableIds = getAvailableSupermarkets().map((sup) => sup.id);
    return subcategorySupermarket.filter((sup) => availableIds.includes(sup.id));
  };

  const getValidatedCategoryValue = () => {
    if (!subcategoryCategory || subcategoryCategory.length === 0) {
      return [];
    }
    const availableIds = getAvailableCategories().map((cat) => cat.id);
    return subcategoryCategory.filter((cat) => availableIds.includes(cat.id));
  };

  useEffect(() => {
    if (subcategoryQuery && subcategoryQuery.trim() !== '' && filteredSupermarkets.length > 0) {
      const availableSupermarketIds = filteredSupermarkets.map((sup) => sup.id);
      const currentSupermarketIds = subcategorySupermarket.map((sup) => sup.id);
      const validatedSupermarketIds = currentSupermarketIds.filter((id) => availableSupermarketIds.includes(id));

      if (validatedSupermarketIds.length !== currentSupermarketIds.length) {
        const validatedSupermarkets = subcategorySupermarket.filter((sup) => validatedSupermarketIds.includes(sup.id));
        dispatch(setSubcategorySupermarket(validatedSupermarkets));
      }
    }

    if (subcategoryQuery && subcategoryQuery.trim() !== '' && filteredCategories.length > 0) {
      const availableCategoryIds = filteredCategories.map((cat) => cat.id);
      const currentCategoryIds = subcategoryCategory.map((cat) => cat.id);
      const validatedCategoryIds = currentCategoryIds.filter((id) => availableCategoryIds.includes(id));

      if (validatedCategoryIds.length !== currentCategoryIds.length) {
        const validatedCategories = subcategoryCategory.filter((cat) => validatedCategoryIds.includes(cat.id));
        dispatch(setSubcategoryCategory(validatedCategories));
      }
    }
  }, [subcategoryQuery, filteredSupermarkets, filteredCategories, subcategorySupermarket, subcategoryCategory, dispatch]);

  const handlePageChange = (event, newPage) => {
    dispatch(setSubcategoryPage(newPage));
  };

  const handleLimitChange = (event) => {
    dispatch(setSubcategoryLimit(event.target.value));
  };

  const handleChange = (type, newVal) => {
    if (!!newVal) {
      const ides = newVal.map((val) => val.id);
      dispatch(setSubcategoryPage(0));
      if (type === 'supermarket') {
        dispatch(setSubcategorySupermarket(newVal));
        dispatch(setSubcategoryCategory([]));
        if (ides.length === 0) {
          setCategories(originalCategories);
        } else {
          setCategories(
            originalCategories.filter((item) => ides.includes(item.supermarket_id)).sort((a, b) => alphabeticSort(a, b)),
          );
        }
      } else if (type === 'category') {
        dispatch(setSubcategoryCategory(newVal));
      }
    }
  };

  const pageChange = (subcat) => {
    const subcategory = { id: subcat.id, name: subcat.name, supermarket_id: subcat.Supermarket.id };
    const supermarket = subcat.Supermarket;
    dispatch(setProductQuery(''));
    dispatch(setProductBrand([]));
    dispatch(setProductCategory([]));
    dispatch(setProductSubcategory([subcategory]));
    dispatch(setProductPage(0));
    dispatch(setProductLimit(10));
    dispatch(setProductSupermarket([supermarket]));
  };

  return (
    <Card
      className={clsx(classes.rootResults, className)}
      {...rest}
    >
      <Divider />
      <Box
        p={2}
        minHeight={56}
      >
        <Grid container spacing={1} >
          <Grid item xs={2} md={4}>
            <TextField
              className={classes.queryField}
              onChange={handleQueryChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon
                      fontSize="small"
                      color="action"
                    >
                      <SearchIcon />
                    </SvgIcon>
                  </InputAdornment>
                ),
                endAdornment: currentQuery ? (
                  <IconButton size="small" onClick={() => {
                    setCurrentQuery('');
                    dispatch(setSubcategoryQuery(''));
                    dispatch(setSubcategoryPage(0));
                  }}>
                    <XCircleIcon />
                  </IconButton>
                ) : null,
              }}
              placeholder="Buscar por Subcategoría"
              value={currentQuery}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={0.5} md={0.5}>
            <Button
              color="secondary"
              variant="contained"
              className={classes.searchAction}
              onClick={() => {
                dispatch(setSubcategoryQuery(currentQuery));
                dispatch(setSubcategoryPage(0));
              }}
            >
              <SvgIcon >
                <SearchIcon />
              </SvgIcon>
            </Button>
          </Grid>
          <Grid item xs={2} md={4.5} align='right'>
            {getAvailableSupermarkets().length > 0 && <Autocomplete
              id="supermarkets-autocomplete"
              className={classes.options}
              options={getAvailableSupermarkets()}
              multiple
              value={getValidatedSupermarketValue()}
              getOptionLabel={(option) => option.name}
              onChange={(e, v) => handleChange('supermarket', v)}
              limitTags={1}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.queryField}
                  label='Supermercados'
                  variant='outlined'
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name}
                </li>
              )}
            />
            }
          </Grid>
          <Grid item xs={3} justifySelf="end">
            {getAvailableCategories().length > 0 && <Autocomplete
              id="categories-autocomplete"
              options={getAvailableCategories()}
              multiple
              value={getValidatedCategoryValue()}
              getOptionLabel={(option) => option.name}
              onChange={(e, v) => handleChange('category', v)}
              limitTags={1}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.queryField}
                  label='Categoría'
                  variant='outlined'
                />
              )}
              renderOption={(props, option, { selected }) => {
                const supermarket = getAvailableSupermarkets().find((sup) => sup.id === option.supermarket_id);
                return (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name} {supermarket ? `(${supermarket.name})` : ''}
                  </li>
                );
              }}
            />
            }
          </Grid>
        </Grid>
      </Box>

      <PerfectScrollbar>
        <Box minWidth={700}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align='center'>
                  Subcategoría
                </TableCell>
                <TableCell align='center'>
                  Categoría asociada
                </TableCell>
                <TableCell align='center'>
                  Supermercado
                </TableCell>
                <TableCell align='center'>
                  Productos asociados
                </TableCell>
                <TableCell align='center'>
                  Frecuencia
                </TableCell>
                <TableCell align='center'>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentSubcategories && currentSubcategories.length > 0 ? (
                currentSubcategories.map((subcategory) => (
                  <TableRow
                    hover
                    key={subcategory.id}
                  >
                    <TableCell>{subcategory.name}</TableCell>
                    <TableCell>{subcategory.Category.name}</TableCell>
                    <TableCell align='center'>{subcategory.Supermarket.name}</TableCell>
                    <TableCell align='center'>{subcategory.Products.length}</TableCell>
                    <TableCell align='center'>{subcategory.frequency}</TableCell>
                    <TableCell align='center'>
                      <Tooltip title="Editar Subcategoría">
                        <IconButton
                          component={RouterLink}
                          to={`/config/subcategory/${subcategory.id}/edit`}
                        >
                          <SvgIcon fontSize="small">
                            <SettingsIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ver productos asociados">
                        <IconButton
                          component={RouterLink}
                          to={'/datos/productos'}
                          onClick={() => pageChange(subcategory)}
                        >
                          <SvgIcon fontSize="small">
                            <ArrowIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No se encontraron subcategorías
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={count}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={Math.min(subcategoryPage, Math.max(0, Math.ceil(count / subcategoryLimit) - 1))}
        rowsPerPage={subcategoryLimit}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Filas por página"
      />
    </Card>
  );
}

Results.propTypes = {
  className: PropTypes.string,
  subcategories: PropTypes.array,
  count: PropTypes.number,
  supermarkets: PropTypes.array,
  originalCategories: PropTypes.array,
  categories: PropTypes.array,
  setCategories: PropTypes.func,
  allSearchResults: PropTypes.array,
};

Results.defaultProps = {
  subcategories: [],
};

export default Results;
